import {
  axiosConfig,
  del,
  get,
  put,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import type {
  DonationStatusUpdatePayload,
  PickupUpdateEmailResult,
  PickupUpdatePayload,
  ScrapDonationPayload,
  ScrapDonationRecord,
} from "@project/types/item-donation";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  isApiItemResponse,
  unwrapItem,
} from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/scrap-donations`;
const SILENT: RequestConfig = { showNotification: false };

type PickupUpdateApiResponse =
  | (ApiItemResponse<ScrapDonationRecord> & {
      email?: PickupUpdateEmailResult;
    })
  | ScrapDonationRecord;

function withDonorSent(
  item: ScrapDonationRecord | undefined,
  email?: PickupUpdateEmailResult
): ScrapDonationRecord | undefined {
  if (!item) {
    return undefined;
  }
  if (email?.donor_sent === undefined && item.donor_sent !== undefined) {
    return item;
  }
  return {
    ...item,
    donor_sent: email?.donor_sent ?? item.donor_sent ?? null,
  };
}

function appendIfPresent(fd: FormData, key: string, value: unknown) {
  if (value === undefined || value === null || value === "") {
    return;
  }
  if (value instanceof File) {
    fd.append(key, value);
    return;
  }
  fd.append(key, String(value));
}

function toFormData(payload: Partial<ScrapDonationPayload>): FormData {
  const fd = new FormData();
  appendIfPresent(fd, "full_name", payload.full_name);
  appendIfPresent(fd, "mobile_number", payload.mobile_number);
  appendIfPresent(fd, "email", payload.email);
  appendIfPresent(fd, "item_category", payload.item_category);
  appendIfPresent(fd, "item_name", payload.item_name);
  appendIfPresent(fd, "quantity", payload.quantity);
  appendIfPresent(fd, "item_condition", payload.item_condition ?? "scrap");
  appendIfPresent(fd, "pickup_address", payload.pickup_address);
  appendIfPresent(fd, "area", payload.area);
  appendIfPresent(fd, "landmark", payload.landmark);
  appendIfPresent(fd, "pincode", payload.pincode);
  appendIfPresent(fd, "message", payload.message);
  appendIfPresent(fd, "status", payload.status);
  if (payload.photo) {
    fd.append("photo", payload.photo);
  }
  return fd;
}

class AdminScrapDonationService {
  listPage = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<ScrapDonationRecord>> =>
    fetchListPage<ScrapDonationRecord>(`${BASE}/`, query);

  get = async (id: number): Promise<ScrapDonationRecord | undefined> => {
    const data = await get<
      ApiItemResponse<ScrapDonationRecord> | ScrapDonationRecord
    >(`${BASE}/${id}/`, SILENT_LIST);
    return unwrapItem(data);
  };

  create = async (
    payload: ScrapDonationPayload
  ): Promise<ScrapDonationRecord> => {
    const response = await axiosConfig.post<
      ApiItemResponse<ScrapDonationRecord> | ScrapDonationRecord
    >(`${BASE}/`, toFormData(payload), {
      ...SILENT,
      headers: { "Content-Type": "multipart/form-data" },
    });
    const item = unwrapItem(response.data);
    if (!item) {
      throw new Error("Failed to create scrap donation request.");
    }
    return item;
  };

  update = async (
    id: number,
    payload: Partial<ScrapDonationPayload>
  ): Promise<ScrapDonationRecord | undefined> => {
    const response = await axiosConfig.patch<
      ApiItemResponse<ScrapDonationRecord> | ScrapDonationRecord
    >(`${BASE}/${id}/`, toFormData(payload), {
      ...SILENT,
      headers: { "Content-Type": "multipart/form-data" },
    });
    return unwrapItem(response.data);
  };

  delete = async (id: number): Promise<void> => {
    await del(`${BASE}/${id}/`, SILENT);
  };

  updateStatus = async (
    id: number,
    payload: DonationStatusUpdatePayload
  ): Promise<ScrapDonationRecord | undefined> => {
    const data = await put<
      ApiItemResponse<ScrapDonationRecord> | ScrapDonationRecord,
      DonationStatusUpdatePayload
    >(`${BASE}/${id}/update_status/`, payload, SILENT);
    return unwrapItem(data);
  };

  sendPickupUpdate = async (
    id: number,
    payload: PickupUpdatePayload
  ): Promise<ScrapDonationRecord | undefined> => {
    const data = await put<PickupUpdateApiResponse, PickupUpdatePayload>(
      `${BASE}/${id}/send_pickup_update/`,
      payload,
      SILENT
    );
    const email =
      data && typeof data === "object" && "email" in data
        ? data.email
        : undefined;
    const item = isApiItemResponse<ScrapDonationRecord>(data)
      ? data.data
      : (data as ScrapDonationRecord | undefined);
    return withDonorSent(item, email);
  };
}

export const adminScrapDonationService = new AdminScrapDonationService();
